import { ServiceLink } from '@hub/app/commons/static-links';
import { RecaptchaProvider } from '@hub/app/RecaptchaProvider';
import { m } from '@hub/messages';
import { useFeatureFlag } from '@platform/feature-flags';
import { ExternalLink, Popup, PopupContent, PopupDescription, PopupHeader, PopupTitle } from '@platform/ui';
import { useEffect, useState } from 'react';
import { useAuthUser } from '../AuthContext';
import { EmailCodeForm } from './EmailCodeForm';
import { MethodSelection } from './MethodSelection';
import { PasswordForm } from './PasswordForm';

type Method = 'password' | 'email-code';

interface StepUpDialogProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: (token: string, expiresAt: string) => void;
}

export function StepUpDialog({ open, onCancel, onSuccess }: StepUpDialogProps) {
  const user = useAuthUser();
  const emailCodeEnabled = useFeatureFlag('mfa-email-code');
  const [method, setMethod] = useState<Method | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMethod(null);
      setError(null);
    }
  }, [open]);

  function handleOpenChange(next: boolean) {
    if (!next) {
      onCancel();
    }
  }

  function handleBack() {
    setMethod(null);
    setError(null);
  }

  return (
    <Popup open={open} onOpenChange={handleOpenChange}>
      <PopupContent closeLabel={m.commons_modal_close()}>
        <RecaptchaProvider>
          <PopupHeader>
            <PopupTitle>{m.auth_mfa_page_title()}</PopupTitle>
            <PopupDescription>
              {emailCodeEnabled && method == null ? m.auth_mfa_page_description_select() : m.auth_mfa_page_description()}
            </PopupDescription>
          </PopupHeader>

          {emailCodeEnabled && method == null && <MethodSelection email={user?.email ?? ''} onSelect={setMethod} onCancel={onCancel} />}
          {(!emailCodeEnabled || method === 'password') && (
            <PasswordForm
              email={user?.email ?? ''}
              error={error}
              setError={setError}
              onSuccess={onSuccess}
              onBack={emailCodeEnabled ? handleBack : undefined}
              onCancel={onCancel}
            />
          )}
          {method === 'email-code' && <EmailCodeForm email={user?.email ?? ''} error={error} setError={setError} onBack={handleBack} />}

          <p className="text-center text-muted-foreground text-sm">
            {m.auth_mfa_help_text()}{' '}
            <ExternalLink href={ServiceLink.Help} className="text-primary underline">
              {m.auth_mfa_help_link()}
            </ExternalLink>
          </p>
        </RecaptchaProvider>
      </PopupContent>
    </Popup>
  );
}
